import { FieldAuditType, FieldType, ISourceDataFieldAudit, ISourceDataNote, ISourceDataSubnote, NoteType } from '@/api/dtos';
import { ISourceDataFormFieldComponentProps, ISourceDataVerificationField, ISourceDataVerificationFieldAudit, ISourceDataVerificationItem } from '@/utilities/forms/SourceDataFormProcessor';
import { faClipboardQuestion, faCalendarDays, faUser } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Dialog, Divider, IconButton, Paper, Slide, styled, Tab, Tabs, Toolbar, Typography, lighten, darken, Chip, Tooltip, alpha } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { FC, forwardRef, ReactElement, Ref, useMemo, useState, useEffect } from 'react';
import ConfigurationFormEditInfo, { ConfigurationTitle } from '../../configuration/edit/ConfigurationFormEditInfo';
import SourceDataInfo, { ISourceDataInfoProps, SourceDataBox, SourceDataTitle } from './SourceDataInfo';
import { NoteStatus } from '../../../../api/dtos';
import { DateTime } from 'luxon'
import SourceDataFormFieldValue from './SourceDataFormFieldValue';

const InfoBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

const AuditHistory = styled(Paper)(({theme}) => ({
    margin: theme.spacing(2, 0, 2, 0),
    padding: theme.spacing(2, 2, 0, 2),
    background: theme.palette.mode === 'dark' ? 
        lighten(theme.palette.common.black, 0.18) :
        darken(theme.palette.common.white, 0.03)
}));

const AuditHistoryTitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(2)
}));

const AuditHistoryTypeChip = styled(Chip)(({theme}) => ({
    marginLeft: theme.spacing(2)
}));


const AuditHistoryDateChip = styled(Chip)(({theme}) => ({
    '& svg': {
        transform: 'scale(0.75)'
    },
    marginLeft: theme.spacing(2)
}));

const AuditHistoryDividerBox = styled(Box)(({theme}) => ({
    margin: theme.spacing(0, -2)
}));

const AuditHistoryDetails = styled(Box)(({theme}) => ({
    whiteSpace: 'pre-wrap',
    padding: theme.spacing(2),
    marginLeft: theme.spacing(6),
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const SubnoteBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(-2),
    
    padding: theme.spacing(2, 2, 0, 2)
}));

const SubnoteTitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(-2, -2, 0, -2),
    padding: theme.spacing(2),
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`
}));

const SourceDataAuditHistorysBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2, 2, 0, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
      children: ReactElement;
    },
    ref: Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SourceDataAudit: FC<ISourceDataVerificationFieldAudit & {fieldType?: FieldType}> = ({
    fieldType,
    auditType,
    auditDate,
    newValue,
    oldValue,
    newValueConverted,
    oldValueConverted,
    editor,
    editorUsername
}) => {

    

    const statusLabel =  useMemo(() => {
        switch (auditType) {
            case FieldAuditType.Inserted:
                return <AuditHistoryTypeChip size="small" label="Inserted" color="info" />;
            case FieldAuditType.Updated:
                return <AuditHistoryTypeChip size="small" label="Updated" color="warning" />;
            case FieldAuditType.Deleted:
                return <AuditHistoryTypeChip size="small" label="Deleted" color="error" />;
            default:
                return null;
        }
    }, [auditType]);

    return (
        <AuditHistory>
            <AuditHistoryTitleBox>
                <Box sx={{display: 'flex', flex: '1 1 auto', alignSelf: 'stretch'}}>
                    <Tooltip title="Old Value">
                        <Box sx={{display: 'flex', flex: '1 1 50%', alignSelf: 'stretch'}}>
                            <SourceDataFormFieldValue fieldType={fieldType} formFieldValue={oldValueConverted} oldValue />
                        </Box>
                    </Tooltip>
                    <Tooltip title="New Value">
                        <Box sx={{display: 'flex', flex: '1 1 50%', alignSelf: 'stretch'}}>
                            <SourceDataFormFieldValue fieldType={fieldType} formFieldValue={newValueConverted} newValue />
                        </Box>
                    </Tooltip>
                </Box>
                <Box sx={{ml: 'auto', minWidth: '450px', textAlign: 'right'}}>
                    {
                        !!statusLabel && (
                            <Tooltip title="Type">
                                {statusLabel}
                            </Tooltip>
                        )
                    }
                    {
                        !!auditDate && (
                            <Tooltip title="Date Created">
                                <AuditHistoryDateChip size="small" label={DateTime.fromISO(auditDate).toFormat('dd/MM/yyyy')} icon={<FontAwesomeIcon icon={faCalendarDays} />}/>
                            </Tooltip>
                        ) 
                    }
                    {
                        !!editor && (
                            <Tooltip title="Created By">
                                <AuditHistoryDateChip size="small" label={editor} icon={<FontAwesomeIcon icon={faUser} />}/>
                            </Tooltip>
                        ) 
                    }
                </Box>
            </AuditHistoryTitleBox>
        </AuditHistory>
    )
};

interface ISourceDataAuditHistoryProps {
    item?: ISourceDataVerificationField
    open?: boolean;
    onClose?: () => void;
    audits?: ISourceDataVerificationFieldAudit[];
}

const SourceDataAuditHistory: FC<ISourceDataAuditHistoryProps> = ({
    audits,
    item,
    open,
    onClose
}) => {
    return (
        <>
            <Dialog
                fullScreen
                open={open ?? false}
                onClose={onClose}
                TransitionComponent={Transition}
            >
                <AppBar position='sticky'>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={onClose}
                            aria-label="close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            View Audit History
                        </Typography>
                    </Toolbar>
                </AppBar>
                {
                    open && (
                        <>
                            <SourceDataTitle variant="h2">
                                Details
                            </SourceDataTitle>
                            <InfoBox>
                                <SourceDataInfo
                                    {...item}
                                    dividers
                                />
                            </InfoBox>
                            <SourceDataTitle variant="h2">
                                Audit History
                            </SourceDataTitle>
                            <SourceDataAuditHistorysBox>
                                {
                                    audits?.map((x, i) => {
                                        return (
                                            <SourceDataAudit
                                                key={i}
                                                {...x}
                                                fieldType={item?.formFieldMetadata?.fieldType}
                                            />
                                        )
                                    })
                                }
                            </SourceDataAuditHistorysBox>
                        </>
                    )
                }
            </Dialog>
        </>
    );
}

export default SourceDataAuditHistory;